import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  employeeListService,
  employeeListShortService,
  employeeDetailService,
  employeeCreateService,
  employeeUpdateService,
  employeeUpdateStatusService,
  employeeAssignToAppsService,
  employeeRemoveAccessAppsService,
  employeeImportService,
} from "./services";

type EmployeeListResponse = Awaited<ReturnType<typeof employeeListService>>;
type EmployeeShortListResponse = Awaited<
  ReturnType<typeof employeeListShortService>
>;
type EmployeeDetailResponse = Awaited<ReturnType<typeof employeeDetailService>>;

export const useEmployees = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof employeeListService>[0];
  options?: QueryObserverOptions<unknown, unknown, EmployeeListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "employeeListHooks",
    queryFn: () => employeeListService(params),
    ...options,
  });

  return response as UseQueryResult<EmployeeListResponse>;
};

export const useShortEmployees = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof employeeListShortService>[0];
  options?: QueryObserverOptions<unknown, unknown, EmployeeShortListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "employeeShortListHooks",
    queryFn: () => employeeListShortService(params),
    ...options,
  });

  return response as UseQueryResult<EmployeeShortListResponse>;
};

export const useEmployeeDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof employeeDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, EmployeeDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "employeeDetailHooks",
    queryFn: () => employeeDetailService(params),
    ...options,
  });

  return response as UseQueryResult<EmployeeDetailResponse>;
};

export const useEmployeeUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    EmployeeDetailResponse,
    unknown,
    Parameters<typeof employeeUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "employeeUpdateHooks",
    mutationFn: employeeUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useEmployeeCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    EmployeeDetailResponse,
    unknown,
    Parameters<typeof employeeCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "employeeCreateHooks",
    mutationFn: employeeCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useEmployeeUpdateStatus = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    EmployeeDetailResponse,
    unknown,
    Parameters<typeof employeeUpdateStatusService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "employeeUpdateStatusHooks",
    mutationFn: employeeUpdateStatusService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useEmployeeAssignToApps = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    EmployeeDetailResponse,
    unknown,
    Parameters<typeof employeeAssignToAppsService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "employeeAssignToAppsService",
    mutationFn: employeeAssignToAppsService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useEmployeeRemoveAccessApps = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    EmployeeDetailResponse,
    unknown,
    Parameters<typeof employeeRemoveAccessAppsService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "employeeRemoveAccessAppsService",
    mutationFn: employeeRemoveAccessAppsService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useEmployeeImport = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    EmployeeDetailResponse,
    unknown,
    Parameters<typeof employeeImportService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "employeeImportService",
    mutationFn: employeeImportService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
